import 'twin.macro'
import { graphql, useStaticQuery } from 'gatsby'
import { useLocation } from '@reach/router'
import { useIsClient, useLocalStorage } from 'usehooks-ts'
import { RenderRichTextData, ContentfulRichTextGatsbyReference } from 'gatsby-source-contentful/rich-text'

import RichText from '@/components/ui/rich-text'
import { useLocale } from '@plugins/bw-i18next'

export type BannerProps = {
  contentful_id: string
  content: RenderRichTextData<ContentfulRichTextGatsbyReference>
}

export function Banner({ contentful_id: id, content }: BannerProps) {
  const [isBannerClosed, setIsBannerClosed] = useLocalStorage(`bw-banner-closed-${id}`, typeof window === 'undefined')

  return (
    !isBannerClosed && (
      <div tw="relative bg-indigoBlue text-white text-center align-middle pt-2 pb-3 [:not(:last-of-type)]:border-b border-deepBlue">
        <div tw="tracking-wide leading-4 font-medium ml-5 mr-8 lg:mx-12 flex items-center justify-center [p]:(text-smaller lg:text-base! mb-0) [a]:(text-tealBlue) text-left lg:text-center">
          <RichText body={content} />
        </div>
        <button
          type="button"
          tw="cursor-pointer rounded absolute top-1 right-1 w-6 h-6 md:(top-1/2 -translate-y-1/2 right-4 w-8 h-8) hover:(text-gray-300 bg-gray-700)"
          onClick={() => {
            setIsBannerClosed(true)
          }}
        >
          <span tw="font-bwi text-sm lg:text-base" data-name="bwi-close">
            &#xe93d;
          </span>
        </button>
      </div>
    )
  )
}

export type HeaderBannerProps = {
  isHelpSite?: boolean
}

export default function HeaderBanner({ isHelpSite = false }: HeaderBannerProps) {
  const contentfulHeaderBanners = useStaticQuery<Queries.HeaderBannerQuery>(graphql`
    query HeaderBanner {
      allContentfulHeaderBanner {
        edges {
          node {
            contentful_id
            node_locale
            content {
              raw
              references {
                ...Link
                ...Icon
              }
            }
            pagesToDisplay {
              url
            }

            pagesNotToDisplayOn {
              url
            }
            helpSite
          }
        }
      }
    }
  `)
  const { pathname } = useLocation()
  const { currentContentfulCode } = useLocale()
  const isClient = useIsClient()

  const currentHeaderBanners = contentfulHeaderBanners?.allContentfulHeaderBanner?.edges?.filter(({ node }) => {
    if (node.node_locale !== currentContentfulCode) return false
    if ((!node.helpSite || node.helpSite === 'Hide on Help Site') && isHelpSite) return false
    if (node.helpSite === 'Only show on Help Site' && !isHelpSite) return false
    return (
      ((node.pagesToDisplay || [])?.length === 0 ||
        node.pagesToDisplay?.some((page) => pathname === new URL(page.url, 'https://bitwarden.com').pathname)) &&
      ((node.pagesNotToDisplayOn || [])?.length === 0 ||
        node.pagesNotToDisplayOn?.every((page) => pathname !== new URL(page.url, 'https://bitwarden.com').pathname))
    )
  })

  return isClient && <>{currentHeaderBanners?.map(({ node }) => <Banner key={node.contentful_id} {...node} />)}</>
}
